/**
 * Web implementation of `TokenStorage` backed by `localStorage` and
 * `sessionStorage`. This is the implementation the web app passes into
 * `createApiClient(config)`. React Native supplies its own `expo-secure-store`
 * backed implementation — JWTs in `AsyncStorage` are explicitly forbidden
 * (RFC §4.5).
 *
 * Key mapping mirrors the existing `AuthService.ts` key names so the two
 * implementations share the same physical storage during the transition period.
 */

import type { TokenStorage } from "./index.js";

export const KEYS = {
  kudozToken: "auth_token",
  eatzyToken: "eatzyUserToken",
  kudozRefreshToken: "__kudoz_refresh_token__",
  eatzyRefreshToken: "eatzyRefreshToken",
  eatzyTokenExpiry: "eatzyTokenExpires",
  eatzyRefreshExpiry: "eatzyRefreshExpires",
} as const;

function read(storage: Storage, key: string): string | null {
  try {
    const val = storage.getItem(key);
    if (!val || val === "undefined" || val === "null") return null;
    return val;
  } catch {
    return null;
  }
}

function write(storage: Storage, key: string, value: string | null): void {
  try {
    if (value === null) {
      storage.removeItem(key);
    } else {
      storage.setItem(key, value);
    }
  } catch {
    // ignore quota / private-mode errors
  }
}

/**
 * Returns a `TokenStorage` implementation backed by browser `localStorage`
 * and `sessionStorage`. Call this once at app startup and pass the result
 * into `createApiClient`.
 *
 * ```ts
 * import { createApiClient } from "@kudoz/api-client";
 * import { createWebStorage } from "@kudoz/api-client/webStorage";
 *
 * export const webApiClient = createApiClient({
 *   baseUrl: "",
 *   storage: createWebStorage(),
 *   clientHeader: `kudoz-web/${APP_VERSION}`,
 *   ...
 * });
 * ```
 */
export function createWebStorage(): TokenStorage {
  return {
    async getKudozToken() {
      return read(localStorage, KEYS.kudozToken);
    },
    async setKudozToken(token) {
      write(localStorage, KEYS.kudozToken, token);
    },

    async getEatzyToken() {
      return read(localStorage, KEYS.eatzyToken);
    },
    async setEatzyToken(token) {
      write(localStorage, KEYS.eatzyToken, token);
      // Mirror into session storage keys for legacy code paths that still
      // read directly from sessionStorage (adminEatzyToken / partnerEatzyToken).
      write(sessionStorage, "adminEatzyToken", token);
      write(sessionStorage, "partnerEatzyToken", token);
    },

    async getKudozRefreshToken() {
      return read(localStorage, KEYS.kudozRefreshToken);
    },
    async setKudozRefreshToken(token) {
      write(localStorage, KEYS.kudozRefreshToken, token);
    },

    async getEatzyRefreshToken() {
      return read(localStorage, KEYS.eatzyRefreshToken);
    },
    async setEatzyRefreshToken(token) {
      write(localStorage, KEYS.eatzyRefreshToken, token);
    },

    async getEatzyTokenExpiry() {
      const raw = read(localStorage, KEYS.eatzyTokenExpiry);
      if (!raw) return null;
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    },
    async setEatzyTokenExpiry(expiry) {
      write(
        localStorage,
        KEYS.eatzyTokenExpiry,
        expiry === null ? null : String(expiry)
      );
    },

    async getEatzyRefreshExpiry() {
      const raw = read(localStorage, KEYS.eatzyRefreshExpiry);
      if (!raw) return null;
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    },
    async setEatzyRefreshExpiry(expiry) {
      write(
        localStorage,
        KEYS.eatzyRefreshExpiry,
        expiry === null ? null : String(expiry)
      );
    },

    async clear() {
      [
        KEYS.kudozToken,
        KEYS.eatzyToken,
        KEYS.kudozRefreshToken,
        KEYS.eatzyRefreshToken,
        KEYS.eatzyTokenExpiry,
        KEYS.eatzyRefreshExpiry,
        "auth_user",
        "partnerUserId",
        "partnerRestaurantId",
      ].forEach((k) => {
        try {
          localStorage.removeItem(k);
        } catch {
          /* ignore */
        }
      });
      ["adminEatzyToken", "partnerEatzyToken"].forEach((k) => {
        try {
          sessionStorage.removeItem(k);
        } catch {
          /* ignore */
        }
      });
    },
  };
}
